import { Parser } from "@json2csv/plainjs";
import { format } from "date-fns";

type DataSelector<T> = {
  label: string;
  value: string | ((record: T) => string);
}

type PagableResponse<T> = { total: number, data: T[] }

type PagableRequest<T> = (skip: number, limit: number) => Promise<PagableResponse<T>>

type Config<T> = {
  chunkSize?: number;
  signal?: AbortSignal;
  filename?: string;
  fields: DataSelector<T>[],
  onProgressUpdate?: (current: number, total: number) => void;
}

const DEFAULT_CHUNK_SIZE = 1000;

export async function chunkCSVExport<T>(pagableRequest: PagableRequest<T>, config: Config<T>) {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  let skip = 0;
  const limit = config.chunkSize || DEFAULT_CHUNK_SIZE;
  let max = limit;
  do {
    // check signal before and after request
    config.signal && throwIfAborted(config.signal);
    const { total, data } = await pagableRequest(skip, limit);
    config.signal && throwIfAborted(config.signal);

    // header and BOM should only appear once
    const parserConfig = skip === 0
      ? { fields: config.fields, withBOM: true, header: true }
      : { fields: config.fields, withBOM: false, header: false };
    const parser = new Parser(parserConfig as any);
    // parse data to bytes
    const bytes = encoder.encode(parser.parse(data) + "\n");
    chunks.push(bytes);

    // update skip and max
    max = total;
    skip += limit;
    // call handler
    config.onProgressUpdate && config.onProgressUpdate(Math.min(skip, max), max);
  } while (skip < max);
  downloadCSV(chunks, config.filename);
}

function throwIfAborted(signal: AbortSignal) {
  if (signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}

function downloadCSV(data: Uint8Array[], filename?: string) {
  const blob = new Blob(data, { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  if (filename) {
    a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  } else {
    a.download = `export_${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}.csv`;
  }
  a.style.display = "none";

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
}
